import * as React from 'react';
import {
  SnackbarProvider,
  useSnackbar,
  SnackbarContent,
  OptionsObject,
  SnackbarMessage,
  VariantType,
} from 'notistack';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { styled, SvgIconTypeMap } from '@mui/material';
import { red, green, blue } from '@mui/material/colors';
import { NotificationContent } from './NotificationContent';
import { SnackbarUtilsConfigurator } from '../../common/SnackBarUtils';
import { OverridableComponent } from '@mui/material/OverridableComponent';

interface NotificationProps {
  id: string | number;
  title?: string;
  message: SnackbarMessage;
  variant: Partial<VariantType>;
}

function NotificationProvider({ children }: { children: React.ReactNode }) {
  return (
    <SnackbarProvider
      data-testid="notification"
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <SnackbarUtilsConfigurator />
      {children}
    </SnackbarProvider>
  );
}

function useNotification() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleNotify = (
    title: string,
    message: SnackbarMessage,
    options: OptionsObject | undefined
  ) => {
    return enqueueSnackbar(message, {
      autoHideDuration: 2000,
      ...options,
      content: (key) => {
        return (
          <Snackbar
            id={key}
            title={title}
            message={message}
            variant="success"
          />
        );
      },
    });
  };

  const handleErrorNotify = (
    title: string,
    message: SnackbarMessage,
    options: OptionsObject | undefined
  ) => {
    return enqueueSnackbar(message, {
      autoHideDuration: 2000,
      preventDuplicate: true,
      ...options,
      content: (key) => {
        return (
          <Snackbar id={key} title={title} message={message} variant="error" />
        );
      },
    });
  };

  const handleWarningNotify = (
    title: string,
    message: SnackbarMessage,
    options: OptionsObject | undefined
  ) => {
    return enqueueSnackbar(message, {
      autoHideDuration: 2000,
      preventDuplicate: true,
      ...options,
      content: (key) => {
        return (
          <Snackbar id={key} title={title} message={message} variant="info" />
        );
      },
    });
  };

  return {
    notifySuccess: (
      title: string,
      message: SnackbarMessage,
      options?: OptionsObject | undefined
    ) => handleNotify(title, message, options),
    notifyError: (
      title: string,
      message: SnackbarMessage,
      options?: OptionsObject | undefined
    ) => handleErrorNotify(title, message, options),
    notifyWarning: (
      title: string,
      message: SnackbarMessage,
      options?: OptionsObject | undefined
    ) => handleWarningNotify(title, message, options),
    close: closeSnackbar,
  };
}

type MutationType = 'create' | 'save' | 'deactivate' | 'update' | 'delete';
/**
 * Creates standard mutation notifications
 * @param entityName singular name of the entity being mutated
 */
function useNotificationMutationFactory(
  entityName: string,
  mutationType: MutationType
) {
  const { notifyError: baseNotifyError, notifySuccess: baseNotifySuccess } =
    useNotification();

  const verbSet: Record<MutationType, string> = {
    create: 'Created',
    deactivate: 'Deactivated',
    save: 'Saved',
    update: 'Updated',
    delete: 'Deleted',
  };

  const verb = verbSet[mutationType];
  const lowercaseVerb = verb.toLowerCase();

  const notifySuccess = () =>
    baseNotifySuccess(
      `${entityName} ${verb}`,
      `${entityName} has been successfully ${lowercaseVerb}`
    );
  const notifyError = () => {
    return baseNotifyError('Error', `${entityName} was not ${lowercaseVerb}`);
  };
  return {
    notifySuccess,
    notifyError,
  };
}

const variantLookup: Record<
  string,
  {
    color: string;
    background: string;
    snackBarBackground: string;
    icon: OverridableComponent<SvgIconTypeMap>;
  }
> = {
  success: {
    color: green[900],
    background: green[500],
    snackBarBackground: green[50],
    icon: CheckCircleOutlineOutlinedIcon,
  },
  error: {
    color: red[900],
    background: red[500],
    snackBarBackground: red[100],
    icon: ErrorOutlineOutlinedIcon,
  },
  info: {
    color: blue[900],
    background: blue[500],
    snackBarBackground: blue[50],
    icon: InfoOutlinedIcon,
  },
};

export const Snackbar = React.forwardRef<HTMLDivElement, NotificationProps>(
  ({ id, message, variant, title }, ref) => {
    const SnackBarStyles = styled('button')((props: { variant: string }) => ({
      backgroundColor: `${
        variantLookup[props.variant].snackBarBackground
      } !important`,
      color: `${variantLookup[props.variant].color} !important`,
      borderRadius: '0.25rem',
      padding: '0.75rem',
      border: 'none',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'flex-start',
      minWidth: '25vw',
    }));

    const IconStyle = {
      fontSize: '1.5625rem',
      marginRight: '0.625rem',
      color: `${variantLookup[variant].color}`,
    };

    const renderIcon = (iconVariant: string) => {
      const Icon = variantLookup[iconVariant].icon;
      return <Icon sx={IconStyle} />;
    };

    return (
      <SnackbarContent key={id} ref={ref}>
        <SnackBarStyles variant={variant}>
          {renderIcon(variant)}
          <NotificationContent title={title || ''}>
            {message as React.ReactChild | React.ReactChildren}
          </NotificationContent>
        </SnackBarStyles>
      </SnackbarContent>
    );
  }
);

export {
  NotificationProvider,
  useNotification,
  useNotificationMutationFactory,
};
